// ─────────────────────────────────────────────────────────────────────────────
// WHETHER THIS FILE HAS ITS BORROWER AUTHORIZATION. READ ONLY.
//
// 🚨 THE REQUEST FLOW IS GONE FROM HERE, DELIBERATELY. This cell used to open
// the invite surface: email the borrower a tokenised link, re-send it, upload a
// paper copy. The borrower now signs the authorization IN THE APP as step 2 of
// confirming the file — straight after reading what their broker sent, field by
// field. Nothing an originator presses is in that path any more.
//
// 🔑 WHY MOVING IT WAS THE FIX AND NOT JUST A TIDY-UP. The old flow could send
// the request before the borrower had seen a single field of the file they were
// authorizing Doorly to act on, because the two were separate errands in
// separate inboxes with nothing sequencing them. Signing at the end of
// ratification means the consent is given by somebody who has just read the
// thing — which is what makes it informed rather than a click-through.
//
// 🔑 THE BADGE STAYS, BECAUSE THE STATE IS STILL THE ORIGINATOR'S BUSINESS. The
// authorization is what lets Doorly touch the file's credit at all, so "signed",
// "one of two" and "not yet" are what an LO needs at a glance to know whether a
// file is waiting on their borrower. What they cannot do is act on it — and the
// copy says so rather than leaving them to infer it from a missing button.
//
// ⚠️ Resend invite IS NOT THE CONSENT REQUEST AND SURVIVES. It re-sends the
// REGISTRATION email — the one that gets the borrower into the app at all, and
// the one that was silently failing on every send until the `from` fix. Without
// registration there is no ratification and so no authorization; this is the
// only button on this cell that still does anything.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

// ⚠️ FOUR STATES, AND "WE COULD NOT LOOK" IS ONE OF THEM. The list endpoint
// reads consent in a separate caught query so a pre-migration database degrades
// to no badge rather than a 500 — which means `consent` can be null, and null is
// not "none on file". Showing "Not requested" there would be a claim nobody
// checked.
export function consentBadge(consent) {
  if (!consent) return { text: 'Consent — not checked', bg: '#f3f4f6', fg: '#6b7280' };
  if (consent.complete) {
    // 🔑 BRAND GREEN, NOT THE INVENTED RAMP. #e3f2ea/#1d6b45 were a
    // forest-green pair that appears nowhere in the palette — see
    // backend/test/brandPalette.test.js, which now covers this file too.
    return { text: consent.signers_expected > 1 ? 'Consent signed (both)' : 'Consent signed',
      bg: 'var(--color-success-light)', fg: 'var(--color-success-text)' };
  }
  // 🔑 PARTIAL IS NOT DONE. A co-borrower is an applicant and their report is
  // their own; the credit gate holds until every signer has authorised, so a
  // file with one of two is still blocked and says so.
  if (consent.signed > 0) {
    return { text: `Consent ${consent.signed} of ${consent.signers_expected}`,
      bg: '#fffbeb', fg: '#92400e' };
  }
  // 🚨 THE WORDING CHANGED WITH THE FLOW. "Consent requested" and "not
  // requested" described an errand the originator ran; nobody requests it now —
  // the borrower signs it as the last step of their own review. So the states
  // say what is true of the BORROWER, which is also what the LO can act on:
  // chase the person, not the paperwork.
  if (consent.last_invite_sent_at) {
    return { text: 'Consent — with borrower', bg: '#fffbeb', fg: '#92400e' };
  }
  return { text: 'Consent — not signed yet', bg: '#fde8e4', fg: '#b3321f' };
}

const LINK = {
  border: 'none', background: 'none', padding: 0, color: 'var(--color-brand-blue)',
  fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

/**
 * 🚨 THE OVERLAY CLOSES ON Escape AND ON THE BACKDROP, and it is the backdrop
 * that matters: a modal over a table with no visible close is a trap on a narrow
 * screen where the panel's own buttons may be below the fold.
 */
export function Modal({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // ⚠️ THE PAGE BEHIND DOES NOT SCROLL while this is open, or a trackpad
    // gesture scrolls the table under a fixed panel and the row the originator
    // opened is no longer the row they are reading.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '6vh 16px 16px', zIndex: 1000, overflowY: 'auto' }}>
      {/* 🔑 THE STOP IS ON THE PANEL, so a click inside it does not close the
          overlay — including a click that lands on the panel's padding. */}
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, width: 28,
              height: 28, fontSize: 15, lineHeight: 1, cursor: 'pointer', color: '#374151',
              flexShrink: 0 }}>×</button>
        </div>
        <div style={{ padding: '14px 18px 18px' }}>{children}</div>
      </div>
    </div>
  );
}

export default function BorrowerConsentCell({
  token, submissionId, consent, align = 'left',
  // 🔑 PASSED IN, NOT RE-FETCHED. The row already knows both, and a component
  // that asked the server again would be a second answer to a question the list
  // has already answered — per row, on a page that can hold dozens.
  registered, invitedAt, borrowerName,
  // 🔑 OPTIONAL. Called after a successful resend so the list can refetch and
  // show the new "Invite sent" date. The component works without it.
  onResent,
  // 🔑 broker gaps (2026-11-02): a withdrawn file has no invite to resend (the server refuses it too).
  withdrawn = false,
}) {
  const [resendOpen, setResendOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  // 🔑 THE CONFIRMATION LIVES IN THE CELL, NOT THE MODAL, because the modal is
  // gone by the time it is shown. See the resend handler for why.
  const [sentNotice, setSentNotice] = useState(null);
  const b = consentBadge(consent);
  // 🔑 NOTHING TO DO ONCE IT IS COMPLETE, so there is no control — but the badge
  // stays, because "signed" is the thing a partner wants to see at a glance.
  const actionable = !consent || !consent.complete;

  // ── 🚨 THE INVITE IS ONLY OFFERED WHILE IT COULD HELP ────────────────────
  //
  // A borrower who has registered has already arrived; sending them an email
  // telling them to create an account is worse than sending nothing. The server
  // refuses it too (409) — this is so the button is not there to be pressed.
  const canResend = registered === false && !withdrawn;

  const resend = useCallback(() => {
    setResending(true);
    setResendMsg(null);
    fetch(`${API_URL}/api/doorlypro/submissions/${submissionId}/resend-invite`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((r) => r.json().then((d) => ({ status: r.status, d })))
      .then(({ status, d }) => {
        // ⚠️ THE SERVER'S SENTENCE, NOT A GENERIC ONE. It distinguishes "already
        // registered", "still awaiting your review" and "too soon" — three
        // things the originator should act on differently.
        // ── 🚨 SUCCESS CLOSES THE MODAL. FAILURE KEEPS IT OPEN. ───────────
        //
        // It used to leave the modal up with a green line AND "Send it again"
        // still live, so the obvious next gesture was to press it a second
        // time — and the thing being spammed is somebody else's inbox. The
        // server rate-limits to one send per two minutes, which bounds the
        // damage; it does not stop the UI inviting the click, and the borrower
        // is the one who pays for a double-tap.
        //
        // 🔑 A FAILURE IS THE OPPOSITE CASE: the server's sentence distinguishes
        // "already registered", "still awaiting your review" and "too soon",
        // which are three different things to do next. Closing the modal would
        // throw that away, so the error stays where it was read.
        if (status === 200 && d.ok) {
          setResendOpen(false);
          setResendMsg(null);
          setSentNotice(`Invite re-sent to ${d.sent_to}.`);
          // ⚠️ THE ROW IS ASKED TO REFRESH, NOT TOLD A NEW DATE. invitedAt is
          // passed in by the list, so this component cannot update it — and
          // writing a local copy would be a second answer to a question the
          // list already owns. Optional: a caller that does not pass it simply
          // keeps the notice until the next load.
          if (typeof onResent === 'function') onResent();
        } else {
          setResendMsg({ ok: false, text: d.error || 'Could not send that invite.' });
        }
      })
      .catch(() => setResendMsg({ ok: false, text: 'Network error — nothing was sent.' }))
      .finally(() => setResending(false));
  }, [submissionId, token, onResent]);

  const invitedLabel = invitedAt === undefined ? null
    : invitedAt ? `Invite sent ${new Date(invitedAt).toLocaleDateString()}`
      : 'No invite sent yet';

  return (
    <div style={{ textAlign: align }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
        borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: b.bg, color: b.fg,
        whiteSpace: 'nowrap' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: b.fg }} />
        {b.text}
      </span>

      {/* 🔑 TWO ACTIONS, SEPARATED BY A DOT. They go to the same person about the
          same file and do different things — one asks for a signature, the other
          re-sends the link that lets them register at all. */}
      {/* ── 🚨 ONE BUTTON, AND IT IS NOT THE CONSENT REQUEST ─────────────
          "Request consent" stood here. It is gone: the borrower signs the
          authorization in the app as step 2 of confirming the file, so there is
          nothing for an originator to send. What remains is the REGISTRATION
          invite — different email, different purpose, and a prerequisite for
          the authorization rather than a substitute for requesting it.

          ⚠️ `actionable` IS STILL COMPUTED AND STILL USED, for the explanatory
          line below. It no longer gates a control, because there is no longer a
          control for it to gate. */}
      {canResend && (
        <div style={{ marginTop: 5, display: 'flex', gap: 8, flexWrap: 'wrap',
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start', alignItems: 'center' }}>
          {/* ⚠️ THE NOTICE IS CLEARED ON REOPEN. Leaving "✓ sent" on screen under
              an open resend dialog says two things at once. */}
          <button type="button" onClick={() => { setSentNotice(null); setResendOpen(true); }}
            style={LINK}>
            Resend invite
          </button>
        </div>
      )}

      {/* 🔑 WHY THERE IS NOTHING TO PRESS. An originator trained to request
          consent who finds no button concludes the feature broke. This says the
          file is waiting on a person and names what that person has to do. */}
      {actionable && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af', lineHeight: 1.5,
          maxWidth: 240 }}>
          {registered === false
            ? 'They sign it in the app, right after they confirm your file.'
            : 'They sign it at the end of their review — nothing needed from you.'}
        </div>
      )}

      {/* ⚠️ THE REGISTRATION STATE IS NAMED, because "Resend invite" with no
          context invites the question it should answer: did one ever go? */}
      {canResend && invitedLabel && (
        <div style={{ marginTop: 3, fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {invitedLabel}
        </div>
      )}

      {/* 🔑 THE SUCCESS MESSAGE, WHERE THE PARTNER IS LOOKING AFTER THE MODAL
          CLOSES. Compact enough for a table cell, and it names the address so
          "did that go to the right person" is answerable without reopening
          anything. */}
      {sentNotice && (
        <div style={{ marginTop: 5, fontSize: 11.5, fontWeight: 700, lineHeight: 1.5,
          color: 'var(--color-success-text)' }}>
          ✓ {sentNotice}
        </div>
      )}

      {resendOpen && (
        <Modal
          title="Resend the registration invite"
          subtitle={borrowerName ? `To ${borrowerName}.` : null}
          onClose={() => { setResendOpen(false); setResendMsg(null); }}>
          <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
            This re-sends the email asking your borrower to create a Doorly account and review
            the file you submitted. It is the same message they were sent when you submitted —
            it carries no figures from the file and no link that signs anything.
          </p>
          {invitedLabel && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#6b7280' }}>{invitedLabel}.</p>
          )}
          {/* 🚨 THE ADDRESS IS NOT EDITABLE HERE. Changing where a submitted
              file's invite goes is changing who the file is about, and that
              belongs to the submission, not to a resend button. */}
          <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="button" onClick={resend} disabled={resending}
              style={{ border: 'none', background: 'var(--color-brand-blue)', color: '#fff',
                borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                cursor: resending ? 'wait' : 'pointer', opacity: resending ? 0.6 : 1 }}>
              {resending ? 'Sending…' : 'Send it again'}
            </button>
            <button type="button" onClick={() => { setResendOpen(false); setResendMsg(null); }}
              style={{ ...LINK, textDecoration: 'none', color: '#6b7280' }}>Cancel</button>
          </div>
          {resendMsg && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.6, fontWeight: 600,
              color: resendMsg.ok ? 'var(--color-success-text)' : '#b3321f' }}>
              {resendMsg.text}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}
